import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  useTheme,
  Divider,
  Stack,
} from '@mui/material';
import {
  Assessment as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompletedIcon,
  Cancel as AbandonedIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';

function FormAnalyticsPage({ darkMode }) {
  const theme = useTheme();
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadForms();
  }, []);

  useEffect(() => {
    if (selectedFormId) {
      loadFormAnalytics();
    }
  }, [selectedFormId]);

  const loadForms = async () => {
    try {
      const response = await fetch('/api/v1/forms/published', {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setForms(data.forms || []);
        if (data.forms?.length > 0) {
          setSelectedFormId(data.forms[0]._id);
        }
      } else {
        setError('Failed to load forms');
      }
    } catch (error) {
      console.error('Error loading forms:', error);
      setError('Error loading forms');
    }
  };

  const loadFormAnalytics = async () => {
    if (!selectedFormId) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/v1/forms/${selectedFormId}/analytics`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      } else {
        // Mock analytics data for now
        setAnalytics({
          totalViews: 1234,
          totalSubmissions: 456,
          completionRate: 37,
          averageTime: 180,
          abandonmentRate: 23,
          conversionRate: 37,
          recentSubmissions: [
            { date: '2024-01-15', count: 12 },
            { date: '2024-01-14', count: 18 },
            { date: '2024-01-13', count: 15 },
            { date: '2024-01-12', count: 22 },
            { date: '2024-01-11', count: 19 },
          ],
          fieldAnalytics: [
            { fieldName: 'Email', completionRate: 95, averageTime: 12 },
            { fieldName: 'Full Name', completionRate: 92, averageTime: 8 },
            { fieldName: 'Phone', completionRate: 78, averageTime: 15 },
            { fieldName: 'Message', completionRate: 65, averageTime: 45 },
          ],
        });
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError('Error loading analytics');
    } finally {
      setLoading(false);
    }
  };

  const selectedForm = forms.find((form) => form._id === selectedFormId);

  if (error && !analytics) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: 3,
      height: '100%',
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column',
    }}
    >
      <Box sx={{
        mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}
      >
        <Typography variant="h4" component="h1">
          Form Analytics
        </Typography>

        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Select Form</InputLabel>
          <Select
            value={selectedFormId}
            onChange={(e) => setSelectedFormId(e.target.value)}
            label="Select Form"
          >
            {forms.map((form) => (
              <MenuItem key={form._id} value={form._id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {form.name}
                  <Chip
                    label={form.status || 'published'}
                    size="small"
                    color="success"
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedForm && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6">{selectedForm.name}</Typography>
          {selectedForm.description && (
            <Typography variant="body2" color="text.secondary">
              {selectedForm.description}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip label={selectedForm.status || 'published'} color="success" size="small" />
            {selectedForm.category && (
              <Chip label={selectedForm.category} variant="outlined" size="small" />
            )}
          </Stack>
        </Paper>
      )}

      {loading ? (
        <Box sx={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh',
        }}
        >
          <CircularProgress />
        </Box>
      ) : analytics ? (
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
        >
          {/* Overview Cards */}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 1,
                  }}
                  >
                    <PeopleIcon color="primary" />
                    <Typography variant="h6">Total Views</Typography>
                  </Box>
                  <Typography variant="h4">{analytics.totalViews.toLocaleString()}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Form page visits
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 1,
                  }}
                  >
                    <CompletedIcon color="success" />
                    <Typography variant="h6">Submissions</Typography>
                  </Box>
                  <Typography variant="h4">{analytics.totalSubmissions.toLocaleString()}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed forms
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 1,
                  }}
                  >
                    <TrendingUpIcon color="info" />
                    <Typography variant="h6">Completion Rate</Typography>
                  </Box>
                  <Typography variant="h4">
                    {analytics.completionRate}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Views to submissions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 1,
                  }}
                  >
                    <ScheduleIcon color="warning" />
                    <Typography variant="h6">Avg. Time</Typography>
                  </Box>
                  <Typography variant="h4">
                    {Math.floor(analytics.averageTime / 60)}
                    m
                    {' '}
                    {analytics.averageTime % 60}
                    s
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Time to complete
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Activity */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Recent Submissions
                  </Typography>
                  <List>
                    {analytics.recentSubmissions.map((submission, index) => (
                      <ListItem key={index} divider={index < analytics.recentSubmissions.length - 1}>
                        <ListItemText
                          primary={new Date(submission.date).toLocaleDateString()}
                          secondary={`${submission.count} submissions`}
                        />
                        <Chip
                          label={submission.count}
                          size="small"
                          color="primary"
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Field Performance
                  </Typography>
                  <List>
                    {analytics.fieldAnalytics.map((field, index) => (
                      <ListItem key={index} divider={index < analytics.fieldAnalytics.length - 1}>
                        <ListItemText
                          primary={field.fieldName}
                          secondary={(
                            <Box component="div">
                              <Typography component="span" variant="body2">
                                Completion:
                                {' '}
                                {field.completionRate}
                                %
                              </Typography>
                              <br />
                              <Typography component="span" variant="body2" color="text.secondary">
                                Avg. time:
                                {' '}
                                {field.averageTime}
                                s
                              </Typography>
                            </Box>
                          )}
                        />
                        <Box sx={{ textAlign: 'right' }}>
                          <Chip
                            label={`${field.completionRate}%`}
                            size="small"
                            color={field.completionRate > 80 ? 'success' : field.completionRate > 60 ? 'warning' : 'error'}
                          />
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Additional Metrics */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 2,
                  }}
                  >
                    <AbandonedIcon color="error" />
                    <Typography variant="h6">Abandonment Rate</Typography>
                  </Box>
                  <Typography variant="h4" color="error.main">
                    {analytics.abandonmentRate}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Users who left without completing
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 2,
                  }}
                  >
                    <TimelineIcon color="info" />
                    <Typography variant="h6">Conversion Rate</Typography>
                  </Box>
                  <Typography variant="h4" color="info.main">
                    {analytics.conversionRate}
                    %
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Goal completion rate
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2, mb: 2,
                  }}
                  >
                    <AnalyticsIcon color="primary" />
                    <Typography variant="h6">Performance Score</Typography>
                  </Box>
                  <Typography variant="h4" color="primary.main">
                    {Math.round((analytics.completionRate + analytics.conversionRate) / 2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overall form effectiveness
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Select a form to view analytics
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default FormAnalyticsPage;
